<template>
  <div>
    <vx-card>
      <div class="flex justify-between items-center mb-4">
        <h2>HaioFlash Plans</h2>
        <vs-button color="primary" @click="showCreateDialog = true">
          <feather-icon icon="PlusIcon" class="mr-2"></feather-icon>
          Create Plan
        </vs-button>
      </div>
      
      <!-- Filters -->
      <vs-row class="mb-4">
        <vs-col vs-w="3" vs-sm="6" vs-xs="12">
          <vs-input
            v-model="filters.name"
            class="w-full"
            label-placeholder="Search by name"
            @keyup.enter="loadPlans()"
          ></vs-input>
        </vs-col>
        <vs-col vs-w="3" vs-sm="6" vs-xs="12">
          <vs-select
            v-model="filters.plan_type"
            class="w-full"
            placeholder="Plan Type"
            @change="loadPlans()"
          >
            <vs-select-item value="" text="All Types"></vs-select-item>
            <vs-select-item value="general_purpose" text="General Purpose"></vs-select-item>
            <vs-select-item value="traffic_optimized" text="Traffic Optimized"></vs-select-item>
            <vs-select-item value="cpu_intensive" text="CPU Intensive"></vs-select-item>
            <vs-select-item value="ram_intensive" text="RAM Intensive"></vs-select-item>
            <vs-select-item value="storage_optimized" text="Storage Optimized"></vs-select-item>
            <vs-select-item value="gpu_accelerated" text="GPU Accelerated"></vs-select-item>
          </vs-select>
        </vs-col>
        <vs-col vs-w="2" vs-sm="6" vs-xs="12">
          <vs-select
            v-model="filters.is_active"
            class="w-full"
            placeholder="Status"
            @change="loadPlans()"
          >
            <vs-select-item value="" text="All"></vs-select-item>
            <vs-select-item :value="1" text="Active"></vs-select-item>
            <vs-select-item :value="0" text="Inactive"></vs-select-item>
          </vs-select>
        </vs-col>
        <vs-col vs-w="2" vs-sm="12" vs-xs="12">
          <vs-button color="primary" class="w-full" @click="loadPlans()">
            <feather-icon icon="SearchIcon"></feather-icon>
          </vs-button>
        </vs-col>
      </vs-row>
      
      <!-- Plans Table -->
      <vs-table v-if="plans" :data="plans">
        <template slot="thead">
          <vs-th>ID</vs-th>
          <vs-th>Name</vs-th>
          <vs-th>Type</vs-th>
          <vs-th>CPU</vs-th>
          <vs-th>RAM</vs-th>
          <vs-th>Storage</vs-th>
          <vs-th>Price/Month</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>Actions</vs-th>
        </template>
        
        <template slot-scope="{ data }">
          <vs-tr :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>{{ tr.id }}</vs-td>
            <vs-td>{{ tr.name }}</vs-td>
            <vs-td>
              <vs-chip :color="getPlanTypeColor(tr.plan_type)">
                {{ getPlanTypeLabel(tr.plan_type) }}
              </vs-chip>
            </vs-td>
            <vs-td>{{ tr.vcpus || tr.vcpu }} vCPU</vs-td>
            <vs-td>{{ formatMemory(tr.memory_base_mb || tr.memory_mb) }}</vs-td>
            <vs-td>{{ tr.storage_gb }} GB</vs-td>
            <vs-td>{{ formatPrice(tr.price_monthly || tr.price_per_month) }}</vs-td>
            <vs-td>
              <vs-chip :color="tr.is_active ? 'success' : 'danger'">
                {{ tr.is_active ? 'Active' : 'Inactive' }}
              </vs-chip>
            </vs-td>
            <vs-td>
              <vs-button
                size="small"
                color="primary"
                type="border"
                icon="edit"
                @click="editPlan(tr)"
              ></vs-button>
              <vs-button
                size="small"
                :color="tr.is_active ? 'warning' : 'success'"
                type="border"
                icon="power_settings_new"
                @click="togglePlanStatus(tr.id)"
              ></vs-button>
              <vs-button
                size="small"
                color="danger"
                type="border"
                icon="delete"
                @click="deletePlan(tr.id)"
              ></vs-button>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vx-card>
    
    <!-- Create/Edit Dialog -->
    <vs-popup
      title="Plan Details"
      :active.sync="showCreateDialog"
    >
      <div class="p-4">
        <vs-row>
          <vs-col vs-w="12">
            <vs-input
              v-model="formData.name"
              class="w-full mb-3"
              label="Plan Name"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-select
              v-model="formData.tier"
              class="w-full mb-3"
              label="Tier"
            >
              <vs-select-item value="entry" text="Entry"></vs-select-item>
              <vs-select-item value="mid" text="Mid"></vs-select-item>
              <vs-select-item value="premium" text="Premium"></vs-select-item>
            </vs-select>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-select
              v-model="formData.datacenter_type"
              class="w-full mb-3"
              label="Datacenter Type"
            >
              <vs-select-item value="domestic" text="Domestic"></vs-select-item>
              <vs-select-item value="global" text="Global"></vs-select-item>
              <vs-select-item value="both" text="Both"></vs-select-item>
            </vs-select>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-select
              v-model="formData.plan_type"
              class="w-full mb-3"
              label="Plan Type"
            >
              <vs-select-item value="general_purpose" text="General Purpose"></vs-select-item>
              <vs-select-item value="traffic_optimized" text="Traffic Optimized"></vs-select-item>
              <vs-select-item value="cpu_intensive" text="CPU Intensive"></vs-select-item>
              <vs-select-item value="ram_intensive" text="RAM Intensive"></vs-select-item>
              <vs-select-item value="storage_optimized" text="Storage Optimized"></vs-select-item>
              <vs-select-item value="gpu_accelerated" text="GPU Accelerated"></vs-select-item>
            </vs-select>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-select
              v-model="formData.ipv4_type"
              class="w-full mb-3"
              label="IPv4 Type"
            >
              <vs-select-item value="shared" text="Shared"></vs-select-item>
              <vs-select-item value="dedicated" text="Dedicated"></vs-select-item>
            </vs-select>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-select
              v-model="formData.autoscale_strategy"
              class="w-full mb-3"
              label="Autoscale Strategy"
            >
              <vs-select-item value="included_free" text="Included Free"></vs-select-item>
              <vs-select-item value="optional_addon" text="Optional Addon"></vs-select-item>
              <vs-select-item value="included_premium" text="Included Premium"></vs-select-item>
            </vs-select>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-input
              v-model.number="formData.vcpus"
              class="w-full mb-3"
              label="vCPU Cores"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-input
              v-model.number="formData.memory_base_mb"
              class="w-full mb-3"
              label="Memory Base (MB)"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-input
              v-model.number="formData.memory_burst_mb"
              class="w-full mb-3"
              label="Memory Burst (MB)"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-input
              v-model.number="formData.storage_gb"
              class="w-full mb-3"
              label="Storage (GB)"
              type="number"
            ></vs-input>
          </vs-col>
          
          <!-- GPU Fields (conditional) -->
          <vs-col vs-w="12" v-if="formData.plan_type === 'gpu_accelerated'">
            <h4 class="mb-2">GPU Configuration</h4>
          </vs-col>
          
          <vs-col vs-w="4" vs-sm="12" v-if="formData.plan_type === 'gpu_accelerated'">
            <vs-input
              v-model="formData.gpu_model"
              class="w-full mb-3"
              label="GPU Model (e.g., NVIDIA A100)"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="4" vs-sm="12" v-if="formData.plan_type === 'gpu_accelerated'">
            <vs-input
              v-model.number="formData.gpu_count"
              class="w-full mb-3"
              label="GPU Count"
              type="number"
              min="1"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="4" vs-sm="12" v-if="formData.plan_type === 'gpu_accelerated'">
            <vs-input
              v-model.number="formData.gpu_memory_gb"
              class="w-full mb-3"
              label="GPU Memory (GB)"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="4" vs-sm="12">
            <vs-input
              v-model.number="formData.bandwidth_quota_gb"
              class="w-full mb-3"
              label="Bandwidth Quota (GB)"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="4" vs-sm="12">
            <vs-input
              v-model.number="formData.bandwidth_ingress_mbps"
              class="w-full mb-3"
              label="Ingress (Download) Mbps"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="4" vs-sm="12">
            <vs-input
              v-model.number="formData.bandwidth_egress_mbps"
              class="w-full mb-3"
              label="Egress (Upload) Mbps"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-input
              v-model.number="formData.bandwidth_overage_price"
              class="w-full mb-3"
              label="Bandwidth Overage Price (IRR per GB)"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-input
              v-model.number="formData.price_monthly"
              class="w-full mb-3"
              label="Price per Month (IRR)"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="12">
            <vs-textarea
              v-model="formData.description"
              class="w-full mb-3"
              label="Description"
            ></vs-textarea>
          </vs-col>
          
          <vs-col vs-w="12">
            <vs-switch v-model="formData.is_active" class="mb-3">
              <span slot="on">Active</span>
              <span slot="off">Inactive</span>
            </vs-switch>
          </vs-col>
          
          <vs-col vs-w="12" class="flex justify-end">
            <vs-button
              color="danger"
              type="border"
              @click="showCreateDialog = false"
              class="mr-2"
            >
              Cancel
            </vs-button>
            <vs-button
              color="primary"
              @click="savePlan()"
            >
              {{ formData.id ? 'Update' : 'Create' }}
            </vs-button>
          </vs-col>
        </vs-row>
      </div>
    </vs-popup>
  </div>
</template>

<script>
export default {
  name: 'PlansList',
  data() {
    return {
      plans: [],
      showCreateDialog: false,
      filters: {
        name: '',
        plan_type: '',
        is_active: ''
      },
      pagination: {
        current_page: 1,
        last_page: 1
      },
      formData: {
        id: null,
        name: '',
        slug: '',
        tier: 'entry',
        plan_type: 'general_purpose',
        vcpus: 1,
        memory_base_mb: 512,
        memory_burst_mb: 1024,
        storage_gb: 10,
        gpu_model: '',
        gpu_count: null,
        gpu_memory_gb: null,
        bandwidth_quota_gb: 1000,
        bandwidth_ingress_mbps: 100,
        bandwidth_egress_mbps: 100,
        bandwidth_overage_price: 5000,
        ipv4_type: 'shared',
        autoscale_strategy: 'included_free',
        autoscale_addon_price: null,
        price_monthly: 50000,
        datacenter_type: 'both',
        description: '',
        features: [],
        is_active: true
      }
    }
  },
  mounted() {
    this.loadPlans()
  },
  methods: {
    async loadPlans() {
      try {
        const params = {
          ...this.filters
        }
        const response = await this.$http.get('/admin/haioflash/plans', { params })
        if (response && response.data && response.data.success) {
          this.plans = Array.isArray(response.data.data) ? response.data.data : []
        } else {
          this.plans = []
        }
      } catch (error) {
        this.plans = []
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load plans',
          color: 'danger'
        })
        console.error(error)
      }
    },
    
    editPlan(plan) {
      this.formData = { ...plan }
      this.showCreateDialog = true
    },
    
    async savePlan() {
      try {
        if (this.formData.id) {
          await this.$http.put(`/admin/haioflash/plans/${this.formData.id}`, this.formData)
          this.$vs.notify({
            title: 'Success',
            text: 'Plan updated successfully',
            color: 'success'
          })
        } else {
          await this.$http.post('/admin/haioflash/plans', this.formData)
          this.$vs.notify({
            title: 'Success',
            text: 'Plan created successfully',
            color: 'success'
          })
        }
        this.showCreateDialog = false
        this.resetForm()
        this.loadPlans()
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to save plan',
          color: 'danger'
        })
        console.error(error)
      }
    },
    
    async togglePlanStatus(id) {
      try {
        await this.$http.post(`/admin/haioflash/plans/${id}/toggle-active`)
        this.$vs.notify({
          title: 'Success',
          text: 'Plan status updated',
          color: 'success'
        })
        this.loadPlans()
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to update plan status',
          color: 'danger'
        })
      }
    },
    
    async deletePlan(id) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Delete',
        text: 'Are you sure you want to delete this plan?',
        accept: async () => {
          try {
            await this.$http.delete(`/admin/haioflash/plans/${id}`)
            this.$vs.notify({
              title: 'Success',
              text: 'Plan deleted successfully',
              color: 'success'
            })
            this.loadPlans()
          } catch (error) {
            this.$vs.notify({
              title: 'Error',
              text: 'Failed to delete plan',
              color: 'danger'
            })
          }
        }
      })
    },
    
    resetForm() {
      this.formData = {
        id: null,
        name: '',
        slug: '',
        tier: 'entry',
        plan_type: 'general_purpose',
        vcpus: 1,
        memory_base_mb: 512,
        memory_burst_mb: 1024,
        storage_gb: 10,
        gpu_model: '',
        gpu_count: null,
        gpu_memory_gb: null,
        bandwidth_quota_gb: 1000,
        bandwidth_ingress_mbps: 100,
        bandwidth_egress_mbps: 100,
        bandwidth_overage_price: 5000,
        ipv4_type: 'shared',
        autoscale_strategy: 'included_free',
        autoscale_addon_price: null,
        price_monthly: 50000,
        datacenter_type: 'both',
        description: '',
        features: [],
        is_active: true
      }
    },
    
    getPlanTypeLabel(type) {
      const labels = {
        'general_purpose': 'General',
        'traffic_optimized': 'Traffic',
        'cpu_intensive': 'CPU',
        'ram_intensive': 'RAM',
        'storage_optimized': 'Storage',
        'gpu_accelerated': 'GPU'
      }
      return labels[type] || type || 'General'
    },
    
    getPlanTypeColor(type) {
      const colors = {
        'general_purpose': 'primary',
        'traffic_optimized': 'success',
        'cpu_intensive': 'warning',
        'ram_intensive': 'rgb(138, 43, 226)',
        'storage_optimized': 'dark',
        'gpu_accelerated': 'danger'
      }
      return colors[type] || 'primary'
    },
    
    formatMemory(mb) {
      if (!mb) return '0 MB'
      if (mb >= 1024) {
        return `${(mb / 1024).toFixed(0)} GB`
      }
      return `${mb} MB`
    },
    
    formatStorage(value) {
      if (!value) return '0 GB'
      // Value is already in GB
      return `${value} GB`
    },
    
    formatPrice(price) {
      if (!price) return '0'
      return Number(price).toLocaleString() + ' IRR'
    }
  }
}
</script>
